import React, { useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const ShippingCalculator = ({ cartTotal, onShippingUpdate }) => {
  const [pincode, setPincode] = useState('');
  const [shippingData, setShippingData] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('rapido');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCalculateShipping = async (e) => {
    e.preventDefault();
    
    if (!pincode || pincode.length !== 6) {
      setError('Please enter a valid 6-digit pincode');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await axios.post(`${API_BASE_URL}/api/shipping/calculate`, {
        pincode,
        shippingMethod: selectedMethod,
        cartTotal
      });

      setShippingData(response.data.data);
      setSuccess('✅ Shipping calculated successfully!');
      
      // Update parent component
      if (onShippingUpdate) {
        onShippingUpdate({
          shippingCost: response.data.data.shippingCost,
          method: response.data.data.method,
          estimatedDelivery: response.data.data.estimatedDelivery,
          totalWithShipping: response.data.data.totalWithShipping
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Pincode not available for delivery';
      setError(errorMsg);
      setShippingData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodChange = async (method) => {
    setSelectedMethod(method);
    
    if (shippingData) {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/shipping/calculate`, {
          pincode,
          shippingMethod: method,
          cartTotal
        });

        setShippingData(response.data.data);
        
        if (onShippingUpdate) {
          onShippingUpdate({
            shippingCost: response.data.data.shippingCost,
            method: response.data.data.method,
            estimatedDelivery: response.data.data.estimatedDelivery,
            totalWithShipping: response.data.data.totalWithShipping
          });
        }
      } catch (err) {
        setError('Error updating shipping method');
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md border-2 border-teal-100 w-full">
      <h3 className="text-xl font-bold text-gray-900 mb-4">🚚 Shipping Details</h3>

      {/* Pincode Input */}
      <form onSubmit={handleCalculateShipping} className="mb-6 w-full">
        <div className="flex gap-2 w-full items-stretch">
          <input
            type="text"
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit pincode"
            maxLength="6"
            className="flex-1 min-w-0 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 text-lg"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition disabled:opacity-50 font-semibold"
          >
            {loading ? '⏳ ...' : 'Check'}
          </button>
        </div>
      </form>

      {/* Messages */}
      {error && (
        <div className="p-3 mb-4 bg-red-100 text-red-700 rounded-lg text-sm">
          ⚠️ {error}
        </div>
      )}

      {success && (
        <div className="p-3 mb-4 bg-green-100 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Shipping Options */}
      {shippingData && (
        <div className="space-y-4">
          {/* Location Info */}
          <div className="p-3 bg-blue-50 rounded-lg text-sm">
            <p className="font-semibold text-gray-900">📍 Delivery to:</p>
            <p className="text-gray-700">{shippingData?.area || 'N/A'} ({shippingData?.pincode || 'N/A'})</p>
          </div>

          {/* Shipping Methods */}
          <div className="space-y-3">
            {/* Rapido Option */}
            <div
              onClick={() => handleMethodChange('rapido')}
              className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                selectedMethod === 'rapido'
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 bg-gray-50 hover:border-teal-400'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">🚗</span>
                    Rapido Parcel - Fast Delivery
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    ⚡ Delivery in {shippingData.rapidoDeliveryDays} day(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-teal-600">₹{shippingData.rapidoRate}</p>
                  <input
                    type="radio"
                    name="shipping"
                    value="rapido"
                    checked={selectedMethod === 'rapido'}
                    onChange={() => handleMethodChange('rapido')}
                    className="mt-2 w-4 h-4 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Porter Option */}
            <div
              onClick={() => handleMethodChange('porter')}
              className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                selectedMethod === 'porter'
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 bg-gray-50 hover:border-teal-400'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">🚶</span>
                    Normal Porter - Economy
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    📅 Delivery in {shippingData.porterDeliveryDays} day(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-teal-600">₹{shippingData.porterRate}</p>
                  <input
                    type="radio"
                    name="shipping"
                    value="porter"
                    checked={selectedMethod === 'porter'}
                    onChange={() => handleMethodChange('porter')}
                    className="mt-2 w-4 h-4 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 bg-teal-50 rounded-lg border-2 border-teal-200">
            <div className="space-y-2">
              <div className="flex justify-between text-gray-700">
                <span>Cart Total:</span>
                <span className="font-semibold">₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>
                  {selectedMethod === 'rapido' ? '🚗' : '🚶'} Shipping:
                </span>
                <span className="font-semibold">
                  ₹{(selectedMethod === 'rapido' ? (shippingData?.rapidoRate || 0) : (shippingData?.porterRate || 0)).toFixed(2)}
                </span>
              </div>
              <div className="border-t-2 border-teal-200 pt-2 flex justify-between text-lg">
                <span className="font-bold">Total:</span>
                <span className="font-bold text-teal-600">
                  ₹{((selectedMethod === 'rapido' ? cartTotal + (shippingData?.rapidoRate || 0) : cartTotal + (shippingData?.porterRate || 0))).toLocaleString('en-IN')}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                📅 Estimated Delivery: {shippingData?.estimatedDelivery ? new Date(shippingData.estimatedDelivery).toLocaleDateString('en-IN', { 
                  weekday: 'short', 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                }) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      {!shippingData && (
        <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
          💡 Enter your pincode to see available shipping options and delivery dates
        </div>
      )}
    </div>
  );
};

export default ShippingCalculator;
